import { useState } from 'react';
import { Modal } from '@components/ui/Modal';
import { TextInput } from '@components/ui';
import Button from '@components/ui/Button/Button';
import { useTranslation } from '@hooks/useTranslation';
import styles from './AddReviewerModal.module.scss';

interface AddReviewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: { name: string; email: string }) => void;
  loading?: boolean;
}

export function AddReviewerModal({ isOpen, onClose, onConfirm, loading }: AddReviewerModalProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<{ name?: string; email?: string }>({});

  const handleClose = () => {
    if (loading) return;
    setName('');
    setEmail('');
    setErrors({});
    onClose();
  };

  const handleConfirm = () => {
    const next: { name?: string; email?: string } = {};
    if (!name.trim()) next.name = t('K_VALIDATION_NAME_REQUIRED', 'Name is required');
    if (!email.trim()) next.email = t('K_VALIDATION_EMAIL_REQUIRED', 'Email is required');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      next.email = t('K_VALIDATION_EMAIL_PATTERN', 'Enter a valid email address');
    }
    if (next.name || next.email) {
      setErrors(next);
      return;
    }
    setErrors({});
    onConfirm({ name: name.trim(), email: email.trim() });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('K_SNAPSHOT_ADMIN_ADD_REVIEWER', 'Add Reviewer')}
      size="sm"
      footer={
        <div className={styles.footer}>
          <Button variant="outline" size="sm" onClick={handleClose} disabled={loading}>
            {t('K_CANCEL', 'Cancel')}
          </Button>
          <Button size="sm" onClick={handleConfirm} loading={loading}>
            {t('K_ADD', 'Add')}
          </Button>
        </div>
      }
    >
      <div className={styles.content}>
        <TextInput
          label={t('K_LABEL_NAME', 'Name')}
          isRequired
          placeholder={t('K_PLACEHOLDER_REVIEWER_NAME', 'Enter reviewer name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={errors.name}
          maxLength={255}
        />
        <TextInput
          label={t('K_LABEL_EMAIL', 'Email')}
          isRequired
          type="email"
          placeholder={t('K_PLACEHOLDER_REVIEWER_EMAIL', 'Enter reviewer email')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={errors.email}
          maxLength={255}
        />
      </div>
    </Modal>
  );
}
